import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import type { TypeGetReceivingProgressByItemIdResponse } from '~/types/receive.types';

type TypeFetchReceivingProgressByItemIdParams = {
  ownerCode: string;
  itemId: string;
};

const fetchReceivingProgressByItemId = async ({
  ownerCode,
  itemId,
}: TypeFetchReceivingProgressByItemIdParams): Promise<TypeGetReceivingProgressByItemIdResponse> => {
  const { data } = await axiosInstance.get<TypeGetReceivingProgressByItemIdResponse>(
    `${API_ENDPOINT.RECEIVE.GET_RECEIVING_PROGRESS_ITEM_ID}/${ownerCode}/progress/${itemId}`
  );
  return data;
};

/**
 * [입고관리 > 입고현황] 입고현황 상세 조회 Mutation Hook
 */
export const useGetReceivingProgressByItemIdMutation = () => {
  return useMutation({
    mutationFn: fetchReceivingProgressByItemId,
  });
};

// items 한 응답에 WAITING/PROCEEDING/COMPLETED + isPassed false(reason) + hasIssueImages true 분기를 모두 포함
const normalDetailResponse: TypeGetReceivingProgressByItemIdResponse = {
  data: {
    summary: {
      status: 'PROCEEDING',
      requestCode: 'OR20260513_00001',
      requestDate: '2026.05.10',
      estimatedArrivalDate: '2026.05.13',
      estimatedArrivalTime: '10:00~12:00',
      finalEstimatedArrivalDate: '2026.05.13~2026.05.13',
      receivingClusterName: '평택센터',
      receivingWarehouseName: '평택상온',
      supplierName: '테스트 화주사_첫번째 공급사',
      supplierCode: 'SU000004',
      releaseCodeName: '컬리배차(직배송)',
      xdock: 'N',
    },
    progressCount: {
      totalUnit: 120,
      totalSku: 5,
      passedUnit: 70,
      passedSku: 2,
      issuedUnit: 4,
      issuedSku: 1,
    },
    items: [
      {
        seq: 1,
        requestItemId: 10001,
        hasIssueImages: false,
        status: 'COMPLETED',
        goodsCode: 'EN0000010151',
        nsid: 'NSID0001',
        goodsName: '닥터유 단백질 35g(3PL)',
        requestedQty: 40,
        details: [{ qty: 40, isPassed: true, reason: null, receivedDate: '2026-05-13T10:30:00' }],
      },
      {
        seq: 2,
        requestItemId: 10002,
        hasIssueImages: true,
        status: 'PROCEEDING',
        goodsCode: 'EN0000010152',
        nsid: 'NSID0002',
        goodsName: '닥터유 단백질 40g(3PL)',
        requestedQty: 50,
        details: [
          { qty: 30, isPassed: true, reason: null, receivedDate: '2026-05-13T11:00:00' },
          { qty: 4, isPassed: false, reason: '파손', receivedDate: '2026-05-13T11:05:00' },
        ],
      },
      {
        seq: 3,
        requestItemId: 10003,
        hasIssueImages: false,
        status: 'WAITING',
        goodsCode: 'EN0000010153',
        nsid: 'NSID0003',
        goodsName: '닥터유 단백질 45g(3PL)',
        requestedQty: 30,
        details: [],
      },
    ],
  },
};

registerMockAPI<TypeGetReceivingProgressByItemIdResponse>({
  method: 'get',
  endpoint: '/api-inbound/v1/3pl/receiving/:ownerCode/progress/:itemId',
  label: '입고/입고현황/상세',
  scenarios: [
    { name: '정상 응답 — 입고중/완료/대기 + 파손/이슈이미지 혼합', response: normalDetailResponse },
    {
      name: '잘못된 파라미터 (9001)',
      response: { error: { code: 9001, message: 'Error Occured By Not Valid Parameters' } },
      status: 400,
    },
  ],
});
